import { styled } from '@mui/material';
import Box from '@mui/material/Box';
import { omit } from 'lodash';
import { Fragment, useCallback } from 'react';

import { ELayoutManagerMode, EWidgetSection } from '#shared';

import { useAppDispatch } from '../../redux-store';
import {
  blockSelectors,
  sectionActions,
  sectionSelectors,
  useSelectWidgetInstanceEntities,
} from '../../slices';
import { useSelectWidgetManagerDraggingWidget } from '../../widgetManager';
import { BLOCK_LIMITS } from '../lib';
import { useLayoutMode } from '../model';

import { Block, type BlockProps } from './Block';
import { BlockAddButton } from './BlockAddButton';
import { Droppable } from './Droppable';
import { EmptySectionPlaceholder } from './EmptySectionPlaceholder';

const PREFIX = 'TSMED-LayoutManager-Section';

export const sectionClasses = {
  root: `${PREFIX}-root`,
  label: `${PREFIX}-label`,
  body: `${PREFIX}-body`,
  section: `${PREFIX}-section`,
  block: `${PREFIX}-block`,
  emptyPlaceholder: `${PREFIX}-emptyPlaceholder`,
};

const excludedRootProps: PropertyKey[] = [
  'mode',
  'styleSlots',
  '$isMovingTarget',
];

const Root = styled(Box, {
  shouldForwardProp: prop => !excludedRootProps.includes(prop),
})<{
  mode: ELayoutManagerMode;
  styleSlots?: SectionProps['styleSlots'];
}>(({ theme, styleSlots, mode }) => ({
  [`&.${sectionClasses.root}`]: {
    width: '100%',
    height: '100%',
    minWidth: 0,
    minHeight: 0,
    position: 'relative',
    container: 'section-root / size',
    ...styleSlots?.section?.root,
  },
  [`& .${sectionClasses.body}`]: {
    flex: 1,
    minHeight: 0,
    display: 'flex',
    gap: theme.spacing(mode === ELayoutManagerMode.RENDER ? 1 : 0.5),
    width: '100%',
    height: '100%',
    minWidth: 0,
    container: 'section-body / size',
    ...styleSlots?.section?.body,
  },
  [`& .${sectionClasses.emptyPlaceholder}`]: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100%',
    minHeight: '100px',
    borderRadius: theme.spacing(1),
    color: theme.palette.common.white,
    fontSize: '14px',
    fontStyle: 'italic',
    opacity: 0.6,
  },
}));

export interface SectionProps {
  sectionId: EWidgetSection;
  mode: ELayoutManagerMode;
  sortable?: boolean;
  draggable?: boolean;
  styleSlots?: {
    section?: Partial<Record<keyof typeof sectionClasses, React.CSSProperties>>;
  } & BlockProps['styleSlots'];
}

export const Section = ({ sectionId, mode, styleSlots }: SectionProps) => {
  const {
    selectedPaletteWidget,
    setSelectedWidgetId,
    handleTapPlace,
    selectedWidgetId,
    showToast,
  } = useLayoutMode();
  const section = sectionSelectors.useSelectSectionById(sectionId);
  const allSections = sectionSelectors.useSelectSectionAll();
  const widgetEntities = useSelectWidgetInstanceEntities();
  const blockEntities = blockSelectors.useSelectBlockEntities();
  const draggingWidget = useSelectWidgetManagerDraggingWidget();
  const isDragging = !!draggingWidget;
  const dispatch = useAppDispatch();
  const blockIds = sectionSelectors
    .useSelectSectionBlockIds(sectionId)
    .filter(id => {
      const block = blockEntities[id];
      // Look up widget by instanceId (not block ID) for multi-instance support
      const widget = widgetEntities[block?.instanceId || id];
      // Include widgets that don't exist yet (just created)
      if (!widget) {
        return true;
      }
      // Hide sealed new-cases widgets (they are hidden when aside mode is on)
      // Other sealed widgets (like main geo-map) should still be visible
      return !(widget.sealed && widget.type === 'new-cases');
    })
    // Filter out invisible blocks
    .filter(id => {
      const block = blockEntities[id];
      return block?.visible !== false;
    });

  // Check if we can add more blocks (max 4)
  const canAddBlock = blockIds.length < BLOCK_LIMITS.MAX;

  // Handle adding block at specific position via tap
  const handleAddBlockAtPosition = useCallback(
    (position: number) => {
      if (mode !== ELayoutManagerMode.EDIT) {
        return;
      }
      // Call handleTapPlace which will either place the widget immediately
      // (if one is selected) or set the pending placement position
      handleTapPlace(sectionId, position);
    },
    [handleTapPlace, sectionId, mode],
  );

  if (!section || section.disabled) {
    return null;
  }

  const isColumn = section.direction !== 'row';
  const blockStyleSlots = omit(styleSlots, 'section');

  const handleSectionClick = (e: React.MouseEvent) => {
    if (mode !== ELayoutManagerMode.EDIT) {
      return; // Not in EDIT mode
    }

    // Handle tap-to-place for palette widget (default to end)
    if (selectedPaletteWidget) {
      e.stopPropagation();
      handleTapPlace(sectionId);
      return;
    }

    // Handle tap-to-move for selected widget
    if (selectedWidgetId) {
      e.stopPropagation();
      // Get current section of selected widget
      const currentSection = allSections.find(sec =>
        sec?.blockOrder?.includes(selectedWidgetId),
      );

      if (currentSection) {
        if (currentSection.id !== sectionId) {
          // Get widget details for geo-map validation
          const movedWidget = widgetEntities[selectedWidgetId];

          // Special handling for geo-map: prevent moving between center and sides
          if (movedWidget?.type === 'geo-map') {
            // Prevent moving from center to sides
            if (
              currentSection.id === 'center' &&
              (sectionId === 'left-primary' || sectionId === 'right-primary')
            ) {
              showToast(
                'Cannot move Main Map to side panels. Please use Focus View widget for side panels instead.',
                'error',
              );
              setSelectedWidgetId(null);
              return;
            }

            // Prevent moving from sides to center
            if (
              sectionId === 'center' &&
              (currentSection.id === 'left-primary' ||
                currentSection.id === 'right-primary')
            ) {
              showToast(
                'Cannot move Focus View to center. Use the Main Map widget for the center panel.',
                'error',
              );
              setSelectedWidgetId(null);
              return;
            }
          }

          // Move widget to different section
          dispatch(
            sectionActions.sectionMoveBlock({
              sectionFromId: currentSection.id,
              sectionToId: sectionId,
              blockId: selectedWidgetId,
            }),
          );
        }
        // Clear selection after clicking (whether moved or clicked same section)
        setSelectedWidgetId(null);
      }
    }
  };

  // Show add buttons in EDIT mode when we can add more blocks
  const showAddButtons = mode === ELayoutManagerMode.EDIT && canAddBlock;

  return (
    <Root
      id={sectionId}
      className={sectionClasses.root}
      mode={mode}
      sx={{
        zIndex: isDragging ? 9999 : 'auto',
        cursor:
          mode === ELayoutManagerMode.EDIT &&
          (selectedPaletteWidget || selectedWidgetId)
            ? 'pointer'
            : 'default',
      }}
      styleSlots={styleSlots}
    >
      <Droppable
        id={sectionId}
        onClick={handleSectionClick}
        disabled={mode !== ELayoutManagerMode.EDIT}
      >
        <Box
          className={sectionClasses.body}
          sx={{
            display: 'flex',
            flexDirection: isColumn ? 'column' : 'row',
            flex: 1,
            height: '100%',
            width: '100%',
          }}
        >
          {blockIds.length === 0 ? (
            mode === ELayoutManagerMode.EDIT ? (
              // Render BlockAddButton in center of empty section for actual drop/click handling
              <BlockAddButton
                sectionId={sectionId}
                position={0}
                onClick={handleAddBlockAtPosition}
                disabled={!canAddBlock}
                isEmpty
              />
            ) : mode === ELayoutManagerMode.RENDER ? (
              <EmptySectionPlaceholder testId={`empty-section-${sectionId}`} />
            ) : null
          ) : (
            <>
              {/* Add button at top/start */}
              {showAddButtons && (
                <BlockAddButton
                  sectionId={sectionId}
                  position={0}
                  onClick={handleAddBlockAtPosition}
                  disabled={!canAddBlock}
                />
              )}

              {blockIds.map((blockId, index) => {
                const blockSize = section?.blockSizes?.[blockId] || '1fr';
                return (
                  <Fragment key={blockId}>
                    <Box
                      sx={{
                        flex: isColumn ? 1 : blockSize === '1fr' ? 1 : 'none',
                        flexBasis: isColumn ? 'auto' : blockSize,
                        width: isColumn
                          ? '100%'
                          : blockSize === '1fr'
                            ? 'auto'
                            : blockSize,
                        minHeight: 0,
                        minWidth: 0,
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column', // Block content is always column (widget inside)
                      }}
                    >
                      <Block
                        blockId={blockId}
                        mode={mode}
                        styleSlots={blockStyleSlots}
                      />
                    </Box>

                    {/* Add button between blocks and at the end */}
                    {showAddButtons && (
                      <BlockAddButton
                        sectionId={sectionId}
                        position={index + 1}
                        onClick={handleAddBlockAtPosition}
                        disabled={!canAddBlock}
                      />
                    )}
                  </Fragment>
                );
              })}
            </>
          )}
        </Box>
      </Droppable>
    </Root>
  );
};
